import React, {
  memo,
  useContext, useEffect, useRef, useState
} from 'react';
import GlobalContext from '../../contexts/GlobalContext';
import AcmSuccessIcon from '../SVG/AcmSuccessIcon';
import ReturnIcon from '../SVG/ReturnIcon';
import ClosetIcon from '../SVG/ClosetIcon';
import { useUpdateAppData } from '../../hooks/services/ups';
import { CoachingBarState } from '../../types/coachingBar';
import { generateShippingPlanSizeMsg, handleSlideDown } from '../../utils/coachingBarUtils';
import { useDeliveryTime } from '../../hooks/services/common';

type Props = {
  bodyRef: React.MutableRefObject<HTMLElement>;
  coachingPopupRef: React.RefObject<HTMLDivElement>;
  coachingBarRef: React.RefObject<HTMLDivElement>;
  coachingBarState: CoachingBarState;
  setCoachingBarState: Function;
}

const CoachingBarPopup = (props: Props): JSX.Element => {
  const { globalContext } = useContext(GlobalContext);
  const [deliveryTime, setDeliveryTime] = useState<number | null>(null);
  const [expectedDelivery, setExpectedDelivery] = useState('');
  const [displayAcmSuccessPopup, setAcmSuccessPopup] = useState(false);
  const [coachingOverlayClass, setCoachingOverlayClass] = useState('js-coaching-popup-btn');
  const [updateAppData] = useUpdateAppData();
  const acmSuccessPopupRef = useRef(null);
  const planItems = generateShippingPlanSizeMsg(props.coachingBarState.planSize);
  const isMobile = globalContext.gb.utils.checkDevice('mobile');
  const fetchDeliveryTime = useDeliveryTime({
    enabled: true,
    onSuccess: (data) => {
      setDeliveryTime(data.shipmentTime);
    },
    onError: (error) => {
      console.error('Error fetching delivery time:', error);
    },
  });
  const updateExpectedShipmentDate = (timezone: string, timeInMs: number, day: string): void => {
    const time = new Date(timeInMs).toISOString();
    const payload = [{
      key: 'expectedShipmentDate',
      value: JSON.stringify({ timezone: timezone, time: time, shipByDay: day })
    }];

    updateAppData(payload);
  };
  const getExpectedShipmentDeliveryDay = async (deliveryTime: number): Promise<void> => {
    const timeZoneLocaleInfo = window.caastle.localeInfo.timezoneName;
    const days = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday'
    ];
    const expectedDateLocaleInfo = new Date(deliveryTime);
    const expectedDay = expectedDateLocaleInfo.getDay();

    updateExpectedShipmentDate(timeZoneLocaleInfo, deliveryTime, days[expectedDay]);
    setExpectedDelivery(days[expectedDay]);
  };
  const handleAddMoreToClosetbtn = (e: React.MouseEvent<HTMLDivElement | HTMLButtonElement, MouseEvent>): void => {
    e.preventDefault();

    const pageWrapperElem = document.getElementById('page-wrapper');

    if (pageWrapperElem) {
      pageWrapperElem.classList.remove('faceted-mobile--spacing');
    }

    globalContext.gtm.pushVersionedEvent('2.x', 'CoachingBar', 'ClickAddMoreToClosetButton', null);

    // hides coachingPopup with slideDown Animation
    if (props.coachingPopupRef.current) {
      handleSlideDown(props.coachingPopupRef, 15);
    }

    // hides coachingBar with slideDown Animation
    setTimeout(() => {
      if (props.coachingBarRef.current) {
        handleSlideDown(props.coachingBarRef, 10);
      }
    }, 600);

    setTimeout(() => {
      props.setCoachingBarState({
        ...props.coachingBarState,
        displayCoachingPopup: false,
        displayCoachingBar: false
      });
    }, 700);
    props.bodyRef.current.classList.remove('stop-scroll');
  };
  const handleAddToClosetLaterbtn = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
    event.preventDefault();
    setAcmSuccessPopup(true);
    setCoachingOverlayClass('js-okay-cta');
    props.setCoachingBarState({
      ...props.coachingBarState,
      coachingPopupBtnClass: 'js-go-to-closet'
    });
    globalContext.gtm.pushVersionedEvent('2.x', 'CoachingBar', 'ClickAddToClosetLaterLink', null);
    globalContext.gtm.setVirtualPage('New_Subscriber_Onboarding_Coaching_Bar_ACM_Check_Back_Often');
  };
  const handleOkaybtn = (e: React.MouseEvent<HTMLDivElement | HTMLButtonElement, MouseEvent>): void => {
    e.preventDefault();

    const pageWrapperElem = document.getElementById('page-wrapper');

    if (pageWrapperElem) {
      pageWrapperElem.classList.remove('faceted-mobile--spacing');
    }

    // hides coachingPopup with slideDown Animation
    if (acmSuccessPopupRef.current) {
      handleSlideDown(acmSuccessPopupRef, 10);
    }

    // hides coachingBar with slideDown Animation
    setTimeout(() => {
      if (props.coachingBarRef.current) {
        handleSlideDown(props.coachingBarRef, 10);
      }
    }, 600);

    setTimeout(() => {
      props.setCoachingBarState({
        ...props.coachingBarState,
        displayCoachingPopup: false,
        displayCoachingBar: false
      });
    }, 700);
    props.bodyRef.current.classList.remove('stop-scroll');

    globalContext.gtm.pushVersionedEvent('2.x', 'CoachingBar', 'ClickOkay', null);
  };
  const setCoachingBarPopupVirtalPage = (): void => {
    globalContext.gtm.setVirtualPage('New_Subscriber_Onboarding_Coaching_Bar_ACM');
  };
  const makeApiCalls = (): void => {
    fetchDeliveryTime.refetch();
  };
  useEffect(() => {
    if(!deliveryTime)
      makeApiCalls();
  }, []);
  useEffect(() => {
    if(deliveryTime){
      getExpectedShipmentDeliveryDay(deliveryTime);
      setCoachingBarPopupVirtalPage();

      if (isMobile) {
        window.scrollTo(0, 0);
        props.bodyRef.current.classList.add('stop-scroll');
      } else {
        props.bodyRef.current.classList.add('stop-scroll');
      }
    }
  }, [deliveryTime]);

  const acmPopupTitle = expectedDelivery ? globalContext.i18n.getText('acm_popup_title', { planItems: planItems, expectedDelivery: expectedDelivery }) :  globalContext.i18n.getText('acm_popup_title_soon',{ planItems: planItems});
  const acmPopupHeading = globalContext.i18n.getText('acm_popup_heading');
  const acmPopupMessage1 = globalContext.i18n.getText('acm_popup_message1', { planSize: props.coachingBarState.planSize });
  const acmPopupMessage2 = globalContext.i18n.getText('acm_popup_message2', { recommendedClosetSize: props.coachingBarState.recommendedClosetSize });
  const acmPopupCtaText = globalContext.i18n.getText('acm_popup_cta_text');
  const acmPopupClosetLaterCtaText = globalContext.i18n.getText('acm_popup_closet_later_cta_text');
  const acmSuccessPopupHeading = globalContext.i18n.getText('acm_success_popup_heading');
  const acmSuccessPopupMessage = globalContext.i18n.getText('acm_success_popup_message');
  const acmSuccessPopupCtaText = globalContext.i18n.getText('acm_success_popup_cta_text');

  return (
    <>
    <div
    className={`coaching-overlay js-coaching-overlay ${coachingOverlayClass}`}
    style={{ display: props.coachingBarState.displayCoachingPopup ? 'block' : 'none' }}
    onClick={ displayAcmSuccessPopup ? handleOkaybtn : handleAddMoreToClosetbtn}></div>

    <div className="acm-popup js-coaching-popup" style={{ display: displayAcmSuccessPopup ? 'none' : 'block', height: '0px' }}
    ref={props.coachingPopupRef}>
      <div className="acm-popup__header">
        <div className="acm-popup__header-icon">
          <AcmSuccessIcon />
        </div>
        <h2 className="acm-popup__title">{acmPopupTitle}</h2>
      </div>
      <div className="acm-popup__guideline">
        <div className="acm-popup__guideline-base">
          <h3 className="acm-popup__heading">{acmPopupHeading}</h3>
          <div className="acm-popup__flex-row">
            <div className="acm-popup__guide-icon">
              <ReturnIcon />
            </div>
            <p className="acm-popup__message">{acmPopupMessage1}</p>
          </div>
          <div className="acm-popup__flex-row">
            <div className="acm-popup__guide-icon">
              <ClosetIcon />
              <span className="acm-popup__guide-icon-in-closet">{props.coachingBarState.closetSize}</span>
            </div>
            <p className="acm-popup__message">{acmPopupMessage2}</p>
          </div>
          <div className="acm-popup__cta">
            <button
              className="base-button base-button--medium base-button--activation js-coaching-popup-btn"
              onClick={handleAddMoreToClosetbtn}>
              {acmPopupCtaText}
            </button>
          </div>
          <a href="" className="acm-popup__link js-coaching-popup-link"
            onClick={handleAddToClosetLaterbtn}>
            {acmPopupClosetLaterCtaText}
          </a>
        </div>
      </div>
    </div>
    <div className={`acm-success js-coaching-popup-checkBackOften ${displayAcmSuccessPopup ? '' : 'hide'}`}
    ref={acmSuccessPopupRef}>
      <div className="acm-success__guideline">
        <h3 className="acm-success__heading">{acmSuccessPopupHeading}</h3>
        <div className="acm-success__flex-row">
          <div className="acm-success__guide-icon">
            <ReturnIcon />
          </div>
          <p className="acm-success__message">{acmSuccessPopupMessage}</p>
        </div>
        <div className="acm-success__cta">
          <button className="base-button base-button--medium base-button--activation js-okay-cta"
            onClick={handleOkaybtn}>
            {acmSuccessPopupCtaText}</button>
        </div>
      </div>
    </div>
    </>
  );
};

export default memo(CoachingBarPopup);
